import "./drop-down.scss";

import classNames from "classnames";
import { motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";

import CheckboxIcon from "@client/components/reusable/checkbox-icon/checkbox-icon";
import SvgIcon from "@client/components/reusable/SvgIcons";
import { ReactComponent as Arrow } from "@client/components/reusable/SvgIcons/library/arrow_dropdown.svg";
import useClickAwayMulti from "@client/hooks/useClickAwayMulti";
import { OrderStatuses } from "@client/stores";

export type DropDownOption = {
	id: number;
	label: string;
	status?: OrderStatuses;
};

interface DropDownProps {
	options: DropDownOption[];
	defaultValue?: number;
	defaultSelectedOptions?: DropDownOption[];
	setOptionValueFromParent?: (id: number) => any;
	setSelectedOptionsFromParent?: (selectedOptions: DropDownOption[]) => any;
	useCheckbox?: boolean;
	label: string;
	dropDownWrrapperClassname?: string;
	dropDownClassname?: string;
	dropDownOptionsClassname?: string;

	autohide?: boolean;
}

export const DropDown = observer((props: DropDownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [optionValue, setOptionValue] = useState(
		props.useCheckbox ? 0 : (props.defaultValue as number)
	);

	const dropDownRef = useRef<HTMLDivElement>(null);
	const dropDownOptionsRef = useRef<HTMLDivElement>(null);

	const [selectedOptions, setSelectedOptions] = useState(
		props.useCheckbox ? props.defaultSelectedOptions! : []
	);

	useEffect(() => {
		props.setSelectedOptionsFromParent?.(selectedOptions);
		props.setOptionValueFromParent?.(optionValue);

		if (props.autohide) {
			setIsOpen(false);
		}
	}, [selectedOptions, optionValue]);

	const dropDownVariants = {
		open: { opacity: 1, height: "fit-content" },
		closed: { opacity: 0, height: 0 },
	};

	function triggerDropDown() {
		setIsOpen((prevValue) => !prevValue);
	}

	function handleClearFilter() {
		setSelectedOptions([]);
	}

	function handleOptionClick(id: number) {
		if (props.useCheckbox) {
			const selectedOption = selectedOptions.find((option) => option.id === id);
			if (selectedOption) {
				// remove selected option
				const index = selectedOptions.findIndex(
					(option) => option.id === selectedOption.id
				);
				selectedOptions.splice(index, 1);
			} else {
				// add selected option
				const newValue = props.options.find((option) => option.id == id);
				if (newValue) {
					selectedOptions.push(newValue);
				}
			}

			setSelectedOptions([...selectedOptions]);
		} else {
			setOptionValue(id);
		}
	}

	useClickAwayMulti([dropDownRef, dropDownOptionsRef], () => setIsOpen(false));

	return (
		<div
			className={classNames(
				"drop-down-wrapper",
				props.dropDownWrrapperClassname
			)}
		>
			<div
				className={classNames("drop-down", props.dropDownClassname)}
				onClick={triggerDropDown}
				ref={dropDownRef}
			>
				<div className="drop-down-placeholder">
					{props.useCheckbox
						? props.label
						: props.options[optionValue]
						? props.options[optionValue].label
						: props.label || ""}
					{props.useCheckbox && !!selectedOptions!.length && (
						<div className="total-options-selected">
							{selectedOptions!.length}
						</div>
					)}
				</div>
				<SvgIcon
					className={classNames("drop-down-arrow", { ["up"]: isOpen })}
					component={Arrow}
					width="12"
					height="12"
				/>
			</div>
			<motion.div
				className={classNames(
					"drop-down-options",
					props.dropDownOptionsClassname
				)}
				variants={dropDownVariants}
				exit={{ opacity: 0, height: 0, visibility: "visible" }}
				animate={isOpen ? "open" : "closed"}
				style={{ visibility: isOpen ? "visible" : "hidden" }}
				ref={dropDownOptionsRef}
			>
				{props.options.map((option) => (
					<div
						key={option.id}
						className={classNames("drop-down-option", {
							["selected"]: option.id === optionValue && !props.useCheckbox,
						})}
						onClick={() => handleOptionClick(option.id)}
					>
						{props.useCheckbox && (
							<CheckboxIcon
								checked={
									!!selectedOptions!.find(
										(selectedOption) => selectedOption.id === option.id
									)
								}
								wrapperClassName="icon-wrapper"
							/>
						)}
						{option.label}
					</div>
				))}
				{props.useCheckbox && (
					<div className="clear-filter" onClick={handleClearFilter}>
						Clear filter
					</div>
				)}
			</motion.div>
		</div>
	);
});
